import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useRestaurant } from '../../context/RestaurantContext';
import { 
  Calendar, Clock, Users, Check, X, Phone, Mail, 
  ChevronLeft, ChevronRight, AlertCircle, CheckCircle,
  XCircle, Timer, RefreshCw, Store, UserPlus, MapPin, Trash2, UserX
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import { toast } from 'sonner';

export default function TableBookings() {
  const { api } = useAuth();
  // Use shared restaurant context
  const { restaurants, selectedRestaurant, setSelectedRestaurant } = useRestaurant();
  const [bookings, setBookings] = useState([]);
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  
  // Walk-in dialog state
  const [showWalkInDialog, setShowWalkInDialog] = useState(false);
  const [walkInForm, setWalkInForm] = useState({
    customer_name: '',
    party_size: 2,
    table_id: '',
    notes: ''
  });

  // New Reservation dialog state
  const [showBookingDialog, setShowBookingDialog] = useState(false);
  const [bookingForm, setBookingForm] = useState({
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    party_size: 2,
    date: new Date().toISOString().split('T')[0],
    time: '19:00',
    table_id: '',
    special_requests: ''
  });

  const fetchBookings = useCallback(async () => {
    if (!selectedRestaurant?.id) return;
    setLoading(true);
    try {
      const response = await api.get(`/table-bookings?restaurant_id=${selectedRestaurant.id}`);
      setBookings(response.data);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
    } finally {
      setLoading(false);
    }
  }, [api, selectedRestaurant?.id]);

  const fetchTables = useCallback(async () => {
    if (!selectedRestaurant?.id) return;
    try {
      const response = await api.get(`/restaurants/${selectedRestaurant.id}/tables`);
      setTables(response.data.filter(t => t.is_active));
    } catch (error) {
      console.error('Failed to fetch tables:', error);
    }
  }, [api, selectedRestaurant?.id]);

  useEffect(() => {
    if (selectedRestaurant?.id) {
      fetchBookings();
      fetchTables();
    }
  }, [selectedRestaurant?.id, fetchBookings, fetchTables]);

  const updateBookingStatus = async (bookingId, status) => {
    try {
      await api.put(`/table-bookings/${bookingId}/status`, { status });
      toast.success(`Booking ${status}`);
      fetchBookings();
    } catch (error) {
      toast.error('Failed to update booking');
    }
  };

  // Seat a confirmed reservation (creates table session for Live Tables & POS)
  const handleSeatCustomer = async (booking) => {
    try {
      const response = await api.post(`/table-bookings/${booking.id}/seat`);
      toast.success('Customer seated successfully!', {
        description: 'Customer is now visible in Live Tables and POS.',
        action: {
          label: 'Go to Live Tables',
          onClick: () => window.location.href = '/restaurant/live-tables'
        }
      });
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to seat customer');
    }
  };

  // Delete/archive a booking (clear from list)
  const handleDeleteBooking = async (bookingId) => {
    if (!window.confirm('Remove this booking from the list? (It will be archived for records)')) {
      return;
    }
    
    try {
      await api.delete(`/table-bookings/${bookingId}`);
      toast.success('Booking cleared from list');
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clear booking');
    }
  };

  // Create walk-in booking
  const handleCreateWalkIn = async () => {
    if (!walkInForm.customer_name.trim()) {
      toast.error('Please enter customer name');
      return;
    }
    
    if (!walkInForm.table_id) {
      toast.error('Please select a table');
      return;
    }
    
    try {
      const now = new Date();
      const booking = {
        restaurant_id: selectedRestaurant.id,
        customer_name: walkInForm.customer_name,
        customer_email: '',
        customer_phone: '',
        date: now.toISOString().split('T')[0],
        time: now.toTimeString().slice(0, 5),
        party_size: walkInForm.party_size,
        table_id: walkInForm.table_id,
        special_requests: walkInForm.notes,
        status: 'confirmed',
        booking_type: 'walk_in'
      };
      
      const response = await api.post('/table-bookings', booking);
      
      // Show success with info about Live Tables
      if (response.data.session_id) {
        toast.success('Walk-in customer seated successfully!', {
          description: 'Customer is now visible in Live Tables and POS.',
          action: {
            label: 'Go to Live Tables',
            onClick: () => window.location.href = '/restaurant/live-tables'
          }
        });
      } else {
        toast.success('Walk-in customer seated successfully');
      }
      
      setShowWalkInDialog(false);
      setWalkInForm({ customer_name: '', party_size: 2, table_id: '', notes: '' });
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create walk-in booking');
    }
  };

  // Create a new reservation (for future bookings via phone call, etc.)
  const handleCreateBooking = async () => {
    if (!bookingForm.customer_name.trim()) {
      toast.error('Please enter customer name');
      return;
    }
    
    if (!bookingForm.date || !bookingForm.time) {
      toast.error('Please select date and time');
      return;
    }
    
    try {
      const booking = {
        restaurant_id: selectedRestaurant.id,
        customer_name: bookingForm.customer_name,
        customer_email: bookingForm.customer_email || '',
        customer_phone: bookingForm.customer_phone || '',
        date: bookingForm.date,
        time: bookingForm.time,
        party_size: bookingForm.party_size,
        table_id: bookingForm.table_id || null,
        special_requests: bookingForm.special_requests || '',
        status: 'pending',  // New reservations start as pending
        booking_type: 'reservation'
      };
      
      await api.post('/table-bookings', booking);
      toast.success('Reservation created successfully!', {
        description: `${bookingForm.customer_name} - ${bookingForm.date} at ${bookingForm.time}`
      });
      
      setShowBookingDialog(false);
      setBookingForm({
        customer_name: '',
        customer_phone: '',
        customer_email: '',
        party_size: 2,
        date: new Date().toISOString().split('T')[0],
        time: '19:00',
        table_id: '',
        special_requests: ''
      });
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create reservation');
    }
  };

  // Get available tables (not currently occupied)
  const availableTables = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    const currentHour = new Date().getHours();
    
    // Get tables that are currently occupied
    const occupiedTableIds = new Set(
      bookings
        .filter(b => 
          b.date === today && 
          b.status === 'confirmed' &&
          parseInt(b.time.split(':')[0]) <= currentHour + 2 &&
          parseInt(b.time.split(':')[0]) >= currentHour - 2
        )
        .map(b => b.table_id)
        .filter(Boolean)
    );
    
    return tables.filter(t => !occupiedTableIds.has(t.id));
  }, [tables, bookings]);

  // Stats calculations
  const stats = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    const todayBookings = bookings.filter(b => b.date === today);
    const walkIns = bookings.filter(b => b.booking_type === 'walk_in');
    
    return {
      total: bookings.length,
      pending: bookings.filter(b => b.status === 'pending').length,
      confirmed: bookings.filter(b => b.status === 'confirmed').length,
      todayCount: todayBookings.length,
      todayGuests: todayBookings.reduce((sum, b) => sum + b.party_size, 0),
      upcoming: bookings.filter(b => b.date >= today && ['pending', 'confirmed'].includes(b.status)).length,
      walkIns: walkIns.length,
      todayWalkIns: todayBookings.filter(b => b.booking_type === 'walk_in').length,
    };
  }, [bookings]);

  // Filter bookings by status and date
  const filteredBookings = useMemo(() => {
    let filtered = bookings;
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(b => b.status === statusFilter);
    }
    
    // Sort by date (newest first) then by time
    return filtered.sort((a, b) => {
      const dateCompare = b.date.localeCompare(a.date);
      if (dateCompare !== 0) return dateCompare;
      return a.time.localeCompare(b.time);
    });
  }, [bookings, statusFilter]);

  // Group bookings by date
  const bookingsByDate = useMemo(() => {
    const groups = {};
    filteredBookings.forEach(booking => {
      if (!groups[booking.date]) {
        groups[booking.date] = [];
      }
      groups[booking.date].push(booking);
    });
    return groups;
  }, [filteredBookings]);

  const statusConfig = {
    pending: { color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Timer, label: 'Pending' },
    confirmed: { color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle, label: 'Confirmed' },
    cancelled: { color: 'bg-red-100 text-red-700 border-red-200', icon: XCircle, label: 'Cancelled' },
    completed: { color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Check, label: 'Completed' },
    no_show: { color: 'bg-slate-100 text-slate-700 border-slate-200', icon: AlertCircle, label: 'No Show' },
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (date.getTime() === today.getTime()) {
      return "Today";
    } else if (date.getTime() === tomorrow.getTime()) {
      return "Tomorrow";
    }
    
    return date.toLocaleDateString('en-GB', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long' 
    });
  };

  const isToday = (dateString) => {
    return dateString === new Date().toISOString().split('T')[0];
  };

  // Get table name by ID
  const getTableName = (tableId) => {
    const table = tables.find(t => t.id === tableId);
    return table ? `Table ${table.table_number}` : 'Unassigned';
  };

  if (loading && !bookings.length) {
    return (
      <div className="space-y-6">
        <div className="h-10 bg-slate-200 rounded w-48 animate-pulse"></div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-24 bg-slate-200 rounded-xl animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="table-bookings-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Table Bookings</h1>
          <p className="text-slate-500 mt-1">Manage reservations and walk-in customers</p>
        </div>
        
        <div className="flex items-center gap-3">
          {restaurants.length > 1 && (
            <Select 
              value={selectedRestaurant?.id} 
              onValueChange={(id) => setSelectedRestaurant(restaurants.find(r => r.id === id))}
            >
              <SelectTrigger className="w-48" data-testid="restaurant-selector">
                <Store className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Select restaurant" />
              </SelectTrigger>
              <SelectContent>
                {restaurants.map(r => (
                  <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          
          <Button 
            onClick={() => setShowBookingDialog(true)}
            variant="outline"
            className="border-green-300 text-green-700 hover:bg-green-50"
            data-testid="new-booking-btn"
          >
            <Calendar className="w-4 h-4 mr-2" />
            New Booking
          </Button>
          
          <Button 
            onClick={() => setShowWalkInDialog(true)}
            className="btn-primary"
            data-testid="walk-in-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Walk-In
          </Button>
          
          <Button 
            variant="outline" 
            size="icon" 
            onClick={fetchBookings}
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-orange-600">Today</p>
                <p className="text-3xl font-bold text-orange-700">{stats.todayCount}</p>
                <p className="text-xs text-orange-500">{stats.todayGuests} guests</p>
              </div>
              <Calendar className="w-10 h-10 text-orange-300" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-600">Walk-Ins Today</p>
                <p className="text-3xl font-bold text-purple-700">{stats.todayWalkIns}</p>
                <p className="text-xs text-purple-500">{stats.walkIns} total</p>
              </div>
              <UserPlus className="w-10 h-10 text-purple-300" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-amber-600">Pending</p>
                <p className="text-3xl font-bold text-amber-700">{stats.pending}</p>
                <p className="text-xs text-amber-500">Awaiting response</p>
              </div>
              <Timer className="w-10 h-10 text-amber-300" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600">Confirmed</p>
                <p className="text-3xl font-bold text-green-700">{stats.confirmed}</p>
                <p className="text-xs text-green-500">Ready to serve</p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-300" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600">Upcoming</p>
                <p className="text-3xl font-bold text-blue-700">{stats.upcoming}</p>
                <p className="text-xs text-blue-500">Next 30 days</p>
              </div>
              <Calendar className="w-10 h-10 text-blue-300" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        {['all', 'pending', 'confirmed', 'completed', 'cancelled', 'no_show'].map(status => (
          <Button
            key={status}
            variant={statusFilter === status ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter(status)}
            className={statusFilter === status ? 'btn-primary' : ''}
            data-testid={`filter-${status}`}
          >
            {status === 'all' ? 'All' : statusConfig[status]?.label || status}
            {status !== 'all' && (
              <Badge variant="secondary" className="ml-2">
                {status === 'pending' ? stats.pending : 
                 status === 'confirmed' ? stats.confirmed :
                 bookings.filter(b => b.status === status).length}
              </Badge>
            )}
          </Button>
        ))}
      </div>

      {/* Bookings List */}
      {Object.keys(bookingsByDate).length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Calendar className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No bookings found</h3>
            <p className="text-slate-500 mb-4">
              {statusFilter !== 'all' 
                ? `No ${statusConfig[statusFilter]?.label || statusFilter} bookings`
                : 'Bookings will appear here when customers make reservations'}
            </p>
            <Button onClick={() => setShowWalkInDialog(true)} className="btn-primary">
              <UserPlus className="w-4 h-4 mr-2" />
              Add Walk-In Customer
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(bookingsByDate).map(([date, dateBookings]) => (
            <div key={date}>
              <div className={`flex items-center gap-3 mb-4 ${isToday(date) ? 'text-orange-600' : 'text-slate-600'}`}>
                <Calendar className="w-5 h-5" />
                <h2 className="text-lg font-semibold">{formatDate(date)}</h2>
                <Badge variant="secondary">{dateBookings.length} bookings</Badge>
              </div>
              
              <div className="grid gap-4">
                {dateBookings.map(booking => {
                  const config = statusConfig[booking.status] || statusConfig.pending;
                  const StatusIcon = config.icon;
                  
                  return (
                    <Card 
                      key={booking.id} 
                      className="hover:shadow-md transition-shadow"
                      data-testid={`booking-${booking.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                          <div className="flex items-start gap-4">
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${config.color}`}>
                              <StatusIcon className="w-6 h-6" />
                            </div>
                            
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-semibold text-slate-900">{booking.customer_name}</h3>
                                {booking.booking_type === 'walk_in' && (
                                  <Badge variant="outline" className="text-purple-600 border-purple-300">
                                    <UserPlus className="w-3 h-3 mr-1" />
                                    Walk-In
                                  </Badge>
                                )}
                                <Badge className={config.color}>{config.label}</Badge>
                              </div>
                              
                              <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                                <span className="flex items-center gap-1">
                                  <Clock className="w-4 h-4" />
                                  {booking.time}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Users className="w-4 h-4" />
                                  {booking.party_size} guests
                                </span>
                                {booking.table_id && (
                                  <span className="flex items-center gap-1">
                                    <MapPin className="w-4 h-4" />
                                    {getTableName(booking.table_id)}
                                  </span>
                                )}
                                {booking.customer_phone && (
                                  <span className="flex items-center gap-1">
                                    <Phone className="w-4 h-4" />
                                    {booking.customer_phone}
                                  </span>
                                )}
                                {booking.customer_email && (
                                  <span className="flex items-center gap-1">
                                    <Mail className="w-4 h-4" />
                                    {booking.customer_email}
                                  </span>
                                )}
                              </div>
                              
                              {booking.special_requests && (
                                <p className="text-sm text-slate-500 mt-2 italic">
                                  "{booking.special_requests}"
                                </p>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            {booking.status === 'pending' && (
                              <>
                                <Button
                                  size="sm"
                                  onClick={() => updateBookingStatus(booking.id, 'confirmed')}
                                  className="bg-green-500 hover:bg-green-600 text-white"
                                  data-testid={`confirm-${booking.id}`}
                                >
                                  <Check className="w-4 h-4 mr-1" />
                                  Confirm
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                  onClick={() => updateBookingStatus(booking.id, 'cancelled')}
                                  data-testid={`cancel-${booking.id}`}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                            
                            {booking.status === 'confirmed' && (
                              <>
                                {/* Seat button - for reservations not yet seated */}
                                {booking.booking_type !== 'walk_in' && !booking.session_id && (
                                  <TooltipProvider delayDuration={200}>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          size="sm"
                                          onClick={() => handleSeatCustomer(booking)}
                                          className="bg-green-500 hover:bg-green-600 text-white"
                                          data-testid={`seat-${booking.id}`}
                                        >
                                          <UserPlus className="w-4 h-4 mr-1" />
                                          Seat
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent side="top">
                                        <p className="font-medium">Seat Customer</p>
                                        <p className="text-xs text-slate-400">Start their session in Live Tables & POS</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                )}
                                
                                {/* Already seated indicator */}
                                {booking.session_id && (
                                  <Badge className="bg-green-100 text-green-700 border-green-300">
                                    <Check className="w-3 h-3 mr-1" />
                                    Seated
                                  </Badge>
                                )}
                                
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => updateBookingStatus(booking.id, 'completed')}
                                  data-testid={`complete-${booking.id}`}
                                >
                                  <Check className="w-4 h-4 mr-1" />
                                  Complete
                                </Button>
                                <TooltipProvider delayDuration={200}>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 border-amber-300"
                                        onClick={() => updateBookingStatus(booking.id, 'no_show')}
                                        data-testid={`noshow-${booking.id}`}
                                      >
                                        <UserX className="w-4 h-4 mr-1" />
                                        No Show
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent side="top">
                                      <p className="font-medium">Mark as No Show</p>
                                      <p className="text-xs text-slate-400">Customer didn't arrive for their reservation</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </>
                            )}
                            
                            {/* Clear/Archive button for completed, no_show, or cancelled bookings */}
                            {['completed', 'no_show', 'cancelled'].includes(booking.status) && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-slate-500 hover:text-slate-600 hover:bg-slate-100"
                                onClick={() => handleDeleteBooking(booking.id)}
                                data-testid={`clear-${booking.id}`}
                              >
                                <Trash2 className="w-4 h-4 mr-1" />
                                Clear
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Walk-In Dialog */}
      <Dialog open={showWalkInDialog} onOpenChange={setShowWalkInDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-purple-500" />
              Seat Walk-In Customer
            </DialogTitle>
            <DialogDescription>
              Quickly allocate a table to a walk-in customer
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="customer_name">Customer Name *</Label>
              <Input
                id="customer_name"
                placeholder="Enter customer name"
                value={walkInForm.customer_name}
                onChange={(e) => setWalkInForm(prev => ({ ...prev, customer_name: e.target.value }))}
                data-testid="walkin-name"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="party_size">Party Size</Label>
                <Select 
                  value={walkInForm.party_size.toString()} 
                  onValueChange={(v) => setWalkInForm(prev => ({ ...prev, party_size: parseInt(v) }))}
                >
                  <SelectTrigger data-testid="walkin-party-size">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12, 15, 20].map(n => (
                      <SelectItem key={n} value={n.toString()}>{n} {n === 1 ? 'guest' : 'guests'}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="table">Assign Table *</Label>
                <Select 
                  value={walkInForm.table_id} 
                  onValueChange={(v) => setWalkInForm(prev => ({ ...prev, table_id: v }))}
                  disabled={availableTables.length === 0}
                >
                  <SelectTrigger data-testid="walkin-table">
                    <SelectValue placeholder={availableTables.length === 0 ? "No tables available" : "Select table"} />
                  </SelectTrigger>
                  <SelectContent>
                    {availableTables.length === 0 ? (
                      <SelectItem value="no-tables-placeholder" disabled>No tables available</SelectItem>
                    ) : (
                      availableTables.map(table => (
                        <SelectItem key={table.id} value={table.id}>
                          Table {table.table_number} ({table.capacity} seats) - {table.zone}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="notes">Notes (optional)</Label>
              <Input
                id="notes"
                placeholder="Any special requirements..."
                value={walkInForm.notes}
                onChange={(e) => setWalkInForm(prev => ({ ...prev, notes: e.target.value }))}
                data-testid="walkin-notes"
              />
            </div>
            
            {availableTables.length === 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
                <AlertCircle className="w-4 h-4 inline mr-2" />
                All tables are currently occupied. Complete an existing booking to free up a table.
              </div>
            )}
          </div>
          
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowWalkInDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateWalkIn} 
              className="btn-primary"
              disabled={!walkInForm.customer_name || !walkInForm.table_id}
              data-testid="walkin-submit"
            >
              <Check className="w-4 h-4 mr-2" />
              Seat Customer
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* New Booking Dialog (for reservations via phone call, etc.) */}
      <Dialog open={showBookingDialog} onOpenChange={setShowBookingDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-green-500" />
              New Reservation
            </DialogTitle>
            <DialogDescription>
              Create a booking for a future date (e.g., phone reservation)
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Customer Name */}
            <div className="space-y-2">
              <Label htmlFor="booking_name">Customer Name *</Label>
              <Input
                id="booking_name"
                placeholder="Enter customer name"
                value={bookingForm.customer_name}
                onChange={(e) => setBookingForm(prev => ({ ...prev, customer_name: e.target.value }))}
                data-testid="booking-name"
              />
            </div>
            
            {/* Contact Details */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="booking_phone">Phone</Label>
                <Input
                  id="booking_phone"
                  placeholder="Phone number"
                  value={bookingForm.customer_phone}
                  onChange={(e) => setBookingForm(prev => ({ ...prev, customer_phone: e.target.value }))}
                  data-testid="booking-phone"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="booking_email">Email</Label>
                <Input
                  id="booking_email"
                  type="email"
                  placeholder="Email address"
                  value={bookingForm.customer_email}
                  onChange={(e) => setBookingForm(prev => ({ ...prev, customer_email: e.target.value }))}
                  data-testid="booking-email"
                />
              </div>
            </div>
            
            {/* Date and Time */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="booking_date">Date *</Label>
                <Input
                  id="booking_date"
                  type="date"
                  value={bookingForm.date}
                  min={new Date().toISOString().split('T')[0]}
                  onChange={(e) => setBookingForm(prev => ({ ...prev, date: e.target.value }))}
                  data-testid="booking-date"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="booking_time">Time *</Label>
                <Select 
                  value={bookingForm.time} 
                  onValueChange={(v) => setBookingForm(prev => ({ ...prev, time: v }))}
                >
                  <SelectTrigger data-testid="booking-time">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Generate time slots from 11:00 to 23:00 in 30-min intervals */}
                    {Array.from({ length: 25 }, (_, i) => {
                      const hour = Math.floor(i / 2) + 11;
                      const minute = i % 2 === 0 ? '00' : '30';
                      if (hour > 23) return null;
                      const time = `${hour.toString().padStart(2, '0')}:${minute}`;
                      return (
                        <SelectItem key={time} value={time}>{time}</SelectItem>
                      );
                    }).filter(Boolean)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Party Size and Table */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="booking_party">Party Size *</Label>
                <Select 
                  value={bookingForm.party_size.toString()} 
                  onValueChange={(v) => setBookingForm(prev => ({ ...prev, party_size: parseInt(v) }))}
                >
                  <SelectTrigger data-testid="booking-party-size">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12, 15, 20, 25, 30].map(n => (
                      <SelectItem key={n} value={n.toString()}>{n} {n === 1 ? 'guest' : 'guests'}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="booking_table">Assign Table (optional)</Label>
                <Select 
                  value={bookingForm.table_id} 
                  onValueChange={(v) => setBookingForm(prev => ({ ...prev, table_id: v === 'none' ? '' : v }))}
                >
                  <SelectTrigger data-testid="booking-table">
                    <SelectValue placeholder="Select table" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No table assigned</SelectItem>
                    {tables.map(table => (
                      <SelectItem key={table.id} value={table.id}>
                        Table {table.table_number} ({table.capacity} seats) - {table.zone}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Special Requests */}
            <div className="space-y-2">
              <Label htmlFor="booking_requests">Special Requests (optional)</Label>
              <Input
                id="booking_requests"
                placeholder="Dietary requirements, celebration, etc."
                value={bookingForm.special_requests}
                onChange={(e) => setBookingForm(prev => ({ ...prev, special_requests: e.target.value }))}
                data-testid="booking-requests"
              />
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
              <Calendar className="w-4 h-4 inline mr-2" />
              The booking will be created with <strong>Pending</strong> status. You can confirm it later.
            </div>
          </div>
          
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowBookingDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateBooking} 
              className="bg-green-600 hover:bg-green-700 text-white"
              disabled={!bookingForm.customer_name || !bookingForm.date || !bookingForm.time}
              data-testid="booking-submit"
            >
              <Check className="w-4 h-4 mr-2" />
              Create Booking
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
